/**
 * Titus - Main controller class for TitusJS text-to-speech library
 * Orchestrates all components and provides the public API
 */
import DOMWalker from "./core/DOMWalker.js";
import SpeechController from "./core/SpeechController.js";
import VoiceManager from "./core/VoiceManager.js";
import UIManager from "./ui/UIManager.js";
import ConfigManager from "./core/ConfigManager.js";
import EventManager from "./core/EventManager.js";

export default class Titus extends EventManager {
	/** @type {ConfigManager} Configuration manager instance */
	#configManager;

	/** @type {boolean} Whether the system has been initialized */
	#isInitialized = false;

	/** @type {boolean} Whether speech is currently playing */
	#isPlaying = false;

	/** @type {boolean} Whether speech is currently paused */
	#isPaused = false;

	/** @type {number} Index of the current segment being played */
	#currentSegmentIndex = 0;

	/** @type {Array} Array of content segments to be read */
	#segments = [];

	/** @type {DOMWalker|null} DOM analysis component */
	#domWalker = null;

	/** @type {SpeechController|null} Speech synthesis component */
	#speechController = null;

	/** @type {VoiceManager|null} Voice management component */
	#voiceManager = null;

	/** @type {UIManager|null} User interface component */
	#uiManager = null;

	/** @type {Function} Bound keyboard event handler for cleanup */
	#keyboardHandler;

	/** @type {number|null} Timeout ID for Firefox navigation debounce */
	#firefoxNavigationTimeout = null;

	/**
	 * Initialize Titus with configuration
	 * @param {Object} config - Configuration options
	 */
	constructor(config = {}) {
		super();
		this.#configManager = new ConfigManager(config);
		this.#keyboardHandler = this.#handleKeyboardShortcuts.bind(this);
	}

	/**
	 * Initialize the TitusJS system
	 * @returns {Promise<boolean>} Success status
	 */
	async initialize() {
		try {
			if (!SpeechController.isSupported()) {
				console.warn(
					"TitusJS: Web Speech API not supported by this browser.",
				);
				return false;
			}

			await this.#initializeComponents();
			this.#segments = this.#domWalker.analyzeContent();

			if (this.#segments.length === 0) {
				console.warn("TitusJS: No readable content found on page");
				return false;
			}

			this.#setupEventHandlers();

			if (this.#configManager.get("autoShow")) {
				this.showUI();
			}

			this.#isInitialized = true;

			return true;
		} catch (error) {
			console.error("TitusJS: Initialization failed:", error);
			return false;
		}
	}

	/**
	 * Initialize all component classes
	 * @private
	 */
	async #initializeComponents() {
		const config = this.#configManager.getConfig();

		this.#domWalker = new DOMWalker({
			contentSelector: config.contentSelector,
			...config.dom,
		});

		this.#speechController = new SpeechController();

		this.#voiceManager = new VoiceManager();
		await this.#voiceManager.loadVoices();

		this.#uiManager = new UIManager(config.ui);
	}

	/**
	 * Set up event handlers between components
	 * @private
	 */
	#setupEventHandlers() {
		this.#speechController.addEventListener("onStart", () => {
			this.#isPlaying = true;
			this.#isPaused = false;
			this.#updateUIState();
			this.#highlightCurrentSegment();
		});

		this.#speechController.addEventListener("onEnd", () => {
			this.#isPlaying = false;
			this.#isPaused = false;
			this.#playNextSegment();
		});

		this.#speechController.addEventListener("onPause", () => {
			this.#isPaused = true;
			this.#updateUIState();
		});

		this.#speechController.addEventListener("onResume", () => {
			this.#isPaused = false;
			this.#updateUIState();
		});

		this.#speechController.addEventListener("onError", (data) => {
			console.error("TitusJS: Speech error:", data.error);
			if (this.#isPlaying || this.#isPaused) {
				this.stop();
			}
		});

		this.#uiManager.setCallback("onPlay", () => this.togglePlayPause());
		this.#uiManager.setCallback("onPause", () => this.togglePlayPause());
		this.#uiManager.setCallback("onNext", () => this.next());
		this.#uiManager.setCallback("onPrevious", () => this.previous());
		this.#uiManager.setCallback("onClose", () => this.close());
		this.#uiManager.setCallback("onSettingsChange", (settings) => {
			this.#speechController.updateSettings(settings);
		});

		document.addEventListener("keydown", this.#keyboardHandler);
	}

	/**
	 * Handle keyboard shortcuts
	 * @param {KeyboardEvent} event - Keyboard event
	 * @private
	 */
	#handleKeyboardShortcuts(event) {
		if (!this.#isInitialized) return;

		if (event.altKey) {
			switch (event.key.toLowerCase()) {
				case "p":
					event.preventDefault();
					this.togglePlayPause();
					break;
				case "v":
					event.preventDefault();
					this.next();
					break;
				case "z":
					event.preventDefault();
					this.previous();
					break;
			}
		}
	}

	/**
	 * Show the UI
	 */
	showUI() {
		if (!this.#uiManager) return;
		this.#uiManager.show(this.#getPlayerState());
	}

	/**
	 * Hide the UI
	 */
	hideUI() {
		if (!this.#uiManager) return;
		this.#uiManager.hide();
	}

	/**
	 * Start or resume playback
	 */
	async play() {
		if (!this.#isInitialized || this.#segments.length === 0) return;

		if (this.#isPaused && this.#speechController.getState().isPaused) {
			this.#speechController.resume();
			return;
		}

		const currentSegment = this.#segments[this.#currentSegmentIndex];
		if (!currentSegment) return;

		try {
			const voice = this.#voiceManager.getSelectedVoice();
			await this.#speechController.speak(currentSegment.text, voice);
		} catch (error) {
			console.error("TitusJS: Playback error:", error);
			this.stop();
		}
	}

	/**
	 * Pause playback
	 */
	pause() {
		if (!this.#isInitialized) return;
		this.#speechController.pause();
	}

	/**
	 * Stop playback completely
	 * @param {boolean} force - Whether to force stop
	 */
	stop(force = false) {
		if (!this.#isInitialized) return;
		if (!force && !this.#isPlaying && !this.#isPaused) return;

		this.#speechController.stop(true, false);
		this.#isPlaying = false;
		this.#isPaused = false;
		this.#removeHighlighting();
		this.#updateUIState();
	}

	/**
	 * Toggle between play and pause
	 */
	togglePlayPause() {
		if (!this.#isInitialized) return;

		if (this.#isPlaying && !this.#isPaused) {
			this.pause();
		} else {
			this.play();
		}
	}

	/**
	 * Move to next segment
	 */
	next() {
		if (!this.#isInitialized || this.#segments.length === 0) return;

		if (this.#currentSegmentIndex < this.#segments.length - 1) {
			if (this.#firefoxNavigationTimeout) {
				clearTimeout(this.#firefoxNavigationTimeout);
				this.#firefoxNavigationTimeout = null;
			}

			const wasPlaying = this.#isPlaying;
			const wasPaused = this.#isPaused;

			this.#currentSegmentIndex++;
			this.stop();

			// Firefox-specific: Use debounced navigation to prevent race conditions
			if (this.#isFirefox() && (wasPlaying || wasPaused)) {
				const targetIndex = this.#currentSegmentIndex;
				this.#firefoxNavigationTimeout = setTimeout(() => {
					// Double-check that we're still at the target segment (no race condition)
					if (this.#currentSegmentIndex === targetIndex) {
						this.play();
					}
					this.#firefoxNavigationTimeout = null;
				}, 100);
			} else {
				// Resume playback if it was playing or paused
				if (wasPlaying || wasPaused) {
					this.play();
				} else {
					this.#highlightCurrentSegment();
					this.#updateUIState();
				}
			}
		}
	}

	/**
	 * Move to previous segment
	 */
	previous() {
		if (!this.#isInitialized || this.#segments.length === 0) return;

		if (this.#currentSegmentIndex > 0) {
			if (this.#firefoxNavigationTimeout) {
				clearTimeout(this.#firefoxNavigationTimeout);
				this.#firefoxNavigationTimeout = null;
			}

			const wasPlaying = this.#isPlaying;
			const wasPaused = this.#isPaused;

			this.#currentSegmentIndex--;
			this.stop();

			// Firefox-specific: Use debounced navigation to prevent race conditions
			if (this.#isFirefox() && (wasPlaying || wasPaused)) {
				const targetIndex = this.#currentSegmentIndex;
				this.#firefoxNavigationTimeout = setTimeout(() => {
					// Double-check that we're still at the target segment (no race condition)
					if (this.#currentSegmentIndex === targetIndex) {
						this.play();
					}
					this.#firefoxNavigationTimeout = null;
				}, 100);
			} else {
				// Resume playback if it was playing or paused
				if (wasPlaying || wasPaused) {
					this.play();
				} else {
					this.#highlightCurrentSegment();
					this.#updateUIState();
				}
			}
		}
	}

	/**
	 * Play the next segment automatically
	 * @private
	 */
	async #playNextSegment() {
		// Don't auto-advance if there's a pending Firefox navigation timeout
		// This prevents race conditions when user navigates while speech is ending
		if (this.#firefoxNavigationTimeout) {
			return;
		}

		if (this.#currentSegmentIndex < this.#segments.length - 1) {
			this.#currentSegmentIndex++;
			await this.play();
		} else {
			this.stop();
			this.#currentSegmentIndex = 0;
		}
	}

	/**
	 * Jump to a specific segment
	 * @param {number} index - Segment index
	 */
	goToSegment(index) {
		if (!this.#isInitialized || index < 0 || index >= this.#segments.length)
			return;

		const wasPlaying = this.#isPlaying;
		this.stop();
		this.#currentSegmentIndex = index;

		if (wasPlaying) {
			this.play();
		} else {
			this.#highlightCurrentSegment();
			this.#updateUIState();
		}
	}

	/**
	 * Highlight the current segment
	 * @private
	 */
	#highlightCurrentSegment() {
		const segment = this.#segments[this.#currentSegmentIndex];
		if (!segment || !this.#uiManager) return;

		this.#uiManager.highlightSegment(segment.element);
		this.#uiManager.showBanner(segment.text);
		this.#scrollToSegment(segment.element);
	}

	/**
	 * Remove highlighting
	 * @private
	 */
	#removeHighlighting() {
		if (this.#uiManager) {
			this.#uiManager.removeHighlighting();
			this.#uiManager.hideBanner();
		}
	}

	/**
	 * Scroll element into view smoothly
	 * @param {Element} element - Element to scroll to
	 * @private
	 */
	#scrollToSegment(element) {
		if (!element) return;
		element.scrollIntoView({ behavior: "smooth", block: "center" });
	}

	/**
	 * Update UI state
	 * @private
	 */
	#updateUIState() {
		if (!this.#uiManager) return;
		this.#uiManager.updatePlayerState(this.#getPlayerState());
	}

	/**
	 * Get current player state
	 * @returns {Object} Player state
	 * @private
	 */
	#getPlayerState() {
		return {
			isPlaying: this.#isPlaying,
			isPaused: this.#isPaused,
			currentSegment: this.#currentSegmentIndex,
			totalSegments: this.#segments.length,
			currentText: this.#segments[this.#currentSegmentIndex]?.text || "",
		};
	}

	/**
	 * Close the player and clean up
	 */
	close() {
		this.#isPlaying = false;
		this.#isPaused = false;
		this.stop(true);

		// Additional cleanup for Firefox - multiple attempts with force close flag
		if (this.#speechController) {
			this.#speechController.stop(true, true);

			// Firefox-specific: Additional stop attempts
			if (this.#isFirefox()) {
				setTimeout(() => {
					this.#speechController.stop(true, true);
				}, 10);
				setTimeout(() => {
					this.#speechController.stop(true, true);
				}, 100);
			}
		}

		this.#removeHighlighting();
		this.hideUI();
		document.removeEventListener("keydown", this.#keyboardHandler);
	}

	/**
	 * Detect if the current browser is Firefox
	 * @returns {boolean} Whether the browser is Firefox
	 * @private
	 */
	#isFirefox() {
		return navigator.userAgent.toLowerCase().includes("firefox");
	}

	/**
	 * Rescan content (useful for dynamic pages)
	 * @returns {number} Number of segments found
	 */
	rescanContent() {
		if (!this.#isInitialized || !this.#domWalker) return 0;

		const wasPlaying = this.#isPlaying;
		this.stop();

		this.#segments = this.#domWalker.rescanContent();
		this.#currentSegmentIndex = 0;

		if (wasPlaying && this.#segments.length > 0) {
			this.play();
		}

		this.#updateUIState();
		return this.#segments.length;
	}

	/**
	 * Get information about current state and configuration
	 * @returns {Object} Status information
	 */
	getStatus() {
		return {
			isInitialized: this.#isInitialized,
			isSupported: SpeechController.isSupported(),
			segmentCount: this.#segments.length,
			currentSegment: this.#currentSegmentIndex,
			playerState: this.#getPlayerState(),
			voiceInfo: this.#voiceManager?.getStatus(),
			speechSettings: this.#speechController?.getSettings(),
		};
	}

	/**
	 * Update speech settings
	 * @param {Object} settings - Settings to update
	 */
	updateSpeechSettings(settings) {
		if (this.#speechController) {
			this.#speechController.updateSettings(settings);
		}
	}

	/**
	 * Update UI settings
	 * @param {Object} settings - Settings to update
	 */
	updateUISettings(settings) {
		if (this.#uiManager) {
			this.#uiManager.updateUISettings(settings);
		}
	}

	/**
	 * Set UI callback for external integration
	 * @param {string} eventName - Event name
	 * @param {Function} callback - Callback function
	 */
	setUICallback(eventName, callback) {
		if (this.#uiManager) {
			this.#uiManager.setCallback(eventName, callback);
		}
	}

	/**
	 * Destroy the Titus instance and clean up all resources
	 */
	destroy() {
		this.close();
		if (this.#firefoxNavigationTimeout) {
			clearTimeout(this.#firefoxNavigationTimeout);
			this.#firefoxNavigationTimeout = null;
		}

		if (this.#speechController) {
			this.#speechController.stop();
		}

		if (this.#uiManager) {
			this.#uiManager.destroy();
		}

		this.#isInitialized = false;
		this.#segments = [];
		this.#currentSegmentIndex = 0;
		this.clearAllListeners();
	}
}
